import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import SEO from '../components/SEO';
import ContactFormCompact from '../components/contact/ContactFormCompact';
import PageShell from '../components/site/PageShell';
import { brand, dashboardLoginUrl, images, resolveContactPhone } from '../config/esanzaContent';
import { contentText, useWebsiteContent } from '../hooks/useWebsiteContent';

const ContactPage: React.FC = () => {
  const { t } = useTranslation();
  const { page, settings } = useWebsiteContent('contact');
  const email = contentText(page, 'email', brand.email);
  const phone = contentText(page, 'phone', resolveContactPhone(settings));
  const headline = page?.title || t('site.contact.title', 'Build with us.');
  const subtext = page?.summary || t('site.contact.subtitle', 'Partnerships, pilots, buyers, and field collaboration.');

  return (
  <>
    <SEO
      title="Contact | eSanza"
      description="Talk to eSanza about platform partnerships, pilots, procurement and agricultural operations."
      imagePath={images.traceability}
      keywords="eSanza contact, agricultural platform partnership, farm management software, agricultural procurement"
    />
    <PageShell>
      <section className="px-5 pb-20 pt-32 lg:px-8">
        <div className="mx-auto grid max-w-7xl gap-12 lg:grid-cols-[0.9fr_1.1fr] lg:items-start">
          <div className="contents lg:block">
            <div className="order-1">
            <p className="text-xs font-extrabold uppercase tracking-[0.24em] text-[#d4af37]">{t('nav.contact', 'Contact')}</p>
            <h1 className="mt-5 text-5xl font-black leading-[1] tracking-tight md:text-7xl">
              {headline}
            </h1>
            <p className="mt-6 max-w-xl text-lg leading-8 text-white/68">
              {subtext}
            </p>
            </div>

            <div className="order-3 mt-10 grid gap-4 text-sm text-white/72 lg:order-none">
              <a href={dashboardLoginUrl} className="rounded-lg border border-white/10 bg-white/[0.06] p-5 hover:border-[#d4af37]/50">
                <span className="block text-xs font-extrabold uppercase tracking-[0.18em] text-[#d4af37]">Existing workspace</span>
                <span className="mt-1 block text-base font-black text-white">Login to eSanza dashboard</span>
              </a>
              <Link to="/try-esanza" className="rounded-lg border border-[#d4af37]/30 bg-[#d4af37]/10 p-5 text-[#d4af37] hover:border-[#d4af37]">
                <span className="block text-xs font-extrabold uppercase tracking-[0.18em]">Try eSanza</span>
                <span className="mt-1 block text-base font-black text-white">Start your workspace</span>
              </Link>
              <a href={`mailto:${email}`} className="rounded-lg border border-white/10 bg-white/[0.06] p-5 hover:border-[#d4af37]/50">
                {email}
              </a>
              <a href={`tel:${phone.replace(/\s/g, '')}`} className="rounded-lg border border-white/10 bg-white/[0.06] p-5 hover:border-[#d4af37]/50">
                {phone}
              </a>
              <div className="rounded-lg border border-white/10 bg-white/[0.06] p-5">
                {t('site.contact.registered', '{{company}}, registered in Rwanda', { company: brand.company })}
              </div>
              <div className="rounded-lg border border-white/10 bg-white/[0.06] p-5">
                {brand.domain}
              </div>
            </div>
          </div>

          <div className="order-2 lg:order-none">
            <ContactFormCompact />
          </div>
        </div>
      </section>
    </PageShell>
  </>
  );
};

export default ContactPage;
